import React, { useState } from 'react';

const inputStyle = {
  width: '100%', padding: '11px 12px', border: '1px solid #d9d9d9', borderRadius: 8,
  background: '#fff', fontSize: 14, fontFamily: 'inherit', color: '#111', outline: 'none', boxSizing: 'border-box',
};

const EMPTY = { name: '', phone: '', email: '', postcode: '', service_id: '', message: '', company_website: '' };

export function InstallerLeadFormView({ config, onSubmit, preview = false }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const accent = config.button_color || '#006400';
  const services = (config.services || []).filter((s) => s.enabled !== false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) return setError('Please enter your name.');
    if (!form.phone.trim() && !form.email.trim()) return setError('Please give us a phone number or an email address.');
    if (preview) return setSent(true);
    setBusy(true);
    try {
      await onSubmit({ ...form, email: form.email.trim() || null });
      setSent(true);
      setForm(EMPTY);
    } catch (err) {
      setError(err.message || 'Something went wrong — please try again.');
    } finally {
      setBusy(false);
    }
  };

  const shell = { background: '#fff', border: '1px solid #e5e5e5', borderRadius: 14, overflow: 'hidden', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif' };

  return (
    <div style={shell} data-testid="installer-lead-form">
      <div style={{ padding: '20px 22px 14px', borderBottom: `3px solid ${accent}` }}>
        {config.logo_url && <img src={config.logo_url} alt="" style={{ maxHeight: 44, marginBottom: 8, display: 'block' }} />}
        {config.business_name && <div style={{ fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', color: '#777', fontWeight: 700 }}>{config.business_name}</div>}
        <h1 style={{ margin: '4px 0 2px', fontSize: 22, fontWeight: 800, color: '#111' }}>{config.headline}</h1>
        {config.intro && <p style={{ margin: 0, fontSize: 13, color: '#555' }}>{config.intro}</p>}
      </div>

      {sent ? (
        <div style={{ padding: '32px 22px', textAlign: 'center' }} data-testid="installer-lead-form-success">
          <div style={{ width: 56, height: 56, margin: '0 auto 12px', background: accent, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 28, fontWeight: 800 }}>✓</div>
          <h2 style={{ margin: '0 0 6px', fontSize: 18, color: '#111' }}>{config.thank_you_title}</h2>
          <p style={{ margin: 0, color: '#555', fontSize: 14 }}>{config.thank_you_body}</p>
          {preview && (
            <button type="button" onClick={() => setSent(false)} style={{ marginTop: 16, background: 'transparent', border: `1px solid ${accent}`, color: accent, padding: '6px 14px', borderRadius: 8, fontSize: 12, cursor: 'pointer' }} data-testid="installer-lead-form-preview-reset">
              Back to form (preview)
            </button>
          )}
        </div>
      ) : (
        <form onSubmit={submit} style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Your name *" value={form.name} onChange={set('name')} testid="ilf-name" autoComplete="name" />
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Field label="Phone number" value={form.phone} onChange={set('phone')} testid="ilf-phone" type="tel" autoComplete="tel" />
            <Field label="Email address" value={form.email} onChange={set('email')} testid="ilf-email" type="email" autoComplete="email" />
          </div>
          {config.show_postcode && <Field label="Postcode" value={form.postcode} onChange={set('postcode')} testid="ilf-postcode" autoComplete="postal-code" />}

          {config.show_services && services.length > 0 && (
            <div>
              <Label>What do you need?</Label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 8 }} data-testid="ilf-services">
                {services.map((s) => {
                  const on = form.service_id === s.id;
                  return (
                    <label key={s.id} data-testid={`ilf-service-${s.id}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 10, cursor: 'pointer', border: `2px solid ${on ? accent : '#e5e7eb'}`, background: on ? `${accent}14` : '#fff', fontSize: 13, fontWeight: on ? 700 : 500, color: '#111', transition: 'border-color .15s, background .15s' }}>
                      <input type="radio" name="ilf-service" checked={on} onChange={() => setForm((f) => ({ ...f, service_id: on ? '' : s.id }))} style={{ width: 16, height: 16, accentColor: accent, margin: 0 }} />
                      {s.label}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {config.show_message && (
            <div>
              <Label>Tell us about your project (optional)</Label>
              <textarea value={form.message} onChange={set('message')} rows={4} data-testid="ilf-message" placeholder="Rough size, timescales, anything useful…" style={{ ...inputStyle, resize: 'vertical', minHeight: 90 }} />
            </div>
          )}

          <div style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, overflow: 'hidden' }} aria-hidden="true">
            <label>Company website (leave blank)<input type="text" tabIndex={-1} autoComplete="off" value={form.company_website} onChange={set('company_website')} /></label>
          </div>

          {error && <div data-testid="ilf-error" style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 12px', borderRadius: 8, fontSize: 13 }}>{error}</div>}

          <button type="submit" disabled={busy} data-testid="ilf-submit" style={{ marginTop: 4, background: accent, color: '#fff', border: 0, padding: '13px 16px', borderRadius: 10, fontSize: 15, fontWeight: 800, cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.6 : 1 }}>
            {busy ? 'Sending…' : config.submit_label || 'Send enquiry'}
          </button>
        </form>
      )}
    </div>
  );
}

function Label({ children }) {
  return <div style={{ fontSize: 12, fontWeight: 700, color: '#333', marginBottom: 6 }}>{children}</div>;
}

function Field({ label, value, onChange, testid, type = 'text', autoComplete }) {
  return (
    <div style={{ flex: 1, minWidth: 180 }}>
      <Label>{label}</Label>
      <input type={type} value={value} onChange={onChange} data-testid={testid} style={inputStyle} autoComplete={autoComplete} />
    </div>
  );
}
